<template>
  <div>
    <div v-if="!hideAddress && location?.coordinates" class="mb-2">
      <div class="bg-primary/10 relative gap-1 rounded-md bg-white px-2 pb-1 pt-1.5">
        <div class="font-medium">{{ t('address') }}</div>
        <div class="text-sm">
          {{ location?.address?.displayName }}
        </div>
      </div>
    </div>
    <LabeledText v-else :label="t('address')" class="mb-1">
      <div v-if="location?.coordinates" class="text-success">{{ t('addressIsSet') }} ✓</div>
      <div v-else>
        {{ t('pleaseChooseLocation') }}
      </div>
    </LabeledText>

    <GooglePlacesInput
      v-if="!location?.coordinates || changeAddress"
      @select-address="setLocation"
    />

    <div v-if="!disabled" class="mt-3 flex gap-4">
      <CVButton
        v-if="location?.coordinates && !changeAddress"
        class="flex-grow"
        @click.prevent="changeAddress = true"
      >
        {{ t('changeAddress') }}
      </CVButton>
      <CVButton v-if="loggedInLocation" variant="default" class="flex-grow" @click.prevent="onSave">
        {{ t('confirm') }}
      </CVButton>
      <CVButton
        v-if="changeAddress"
        variant="secondary"
        class="flex-grow"
        @click.prevent="onCancel"
      >
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GeocodedLocation } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { ref } from 'vue';
import GooglePlacesInput, {
  type ExtendedGeocodedLocation,
} from '@/components/GooglePlacesInput.vue';
import LabeledText from '../LabeledText.vue';
import CVButton from '@/components/buttons/CVButton.vue';

const emit = defineEmits<{
  (e: 'update:location', value: ExtendedGeocodedLocation | null): void;
}>();

const { t } = useI18n();

const loggedInLocation = ref<ExtendedGeocodedLocation | null>(null);

const changeAddress = ref(false);

const props = defineProps<{
  location: GeocodedLocation | null | undefined;
  hideAddress?: boolean;
  disabled?: boolean;
  autoConfirmOnSelect?: boolean;
}>();

const normalizeLocation = (location: ExtendedGeocodedLocation | null) =>
  location
    ? {
        ...location,
        coordinates: {
          latitude: parseFloat(location.coordinates?.latitude.toFixed(7) ?? ''),
          longitude: parseFloat(location.coordinates?.longitude.toFixed(7) ?? ''),
        },
      }
    : location;

const setLocation = (location: ExtendedGeocodedLocation | null) => {
  const normalizedLocation = normalizeLocation(location);
  if (props.autoConfirmOnSelect) {
    emit('update:location', normalizedLocation);
    changeAddress.value = false;
    loggedInLocation.value = null;
    return;
  }

  loggedInLocation.value = normalizedLocation;
  changeAddress.value = true;
};

const onSave = () => {
  changeAddress.value = false;
  emit('update:location', loggedInLocation.value);
  loggedInLocation.value = null;
};

const onCancel = () => {
  changeAddress.value = false;
  loggedInLocation.value = null;
};
</script>

<i18n lang="json">
{
  "en": {
    "typeLocation": "Type in Location ...",
    "address": "Address",
    "changeAddress": "Change Address",
    "addressIsSet": "Address is set",
    "pleaseChooseLocation": "Please choose a location"
  },
  "de": {
    "typeLocation": "Gib einen Ort ein ...",
    "address": "Adresse",
    "changeAddress": "Adresse ändern",
    "addressIsSet": "Adresse ist gesetzt",
    "pleaseChooseLocation": "Bitte wähle einen Ort"
  },
  "es": {
    "typeLocation": "Escriba una ubicación…",
    "address": "Dirección",
    "changeAddress": "Cambiar dirección",
    "addressIsSet": "Dirección indicada",
    "pleaseChooseLocation": "Seleccione una ubicación"
  }
}
</i18n>
